import React, { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { useGetArchivedTagListQuery, useGetTagListQuery } from '../../../generated/graphql';
import NewTagDialog from '../../../components/dialogs/NewTagDialog/NewTagDialog';
import TagArchiveDialog from '../../../components/dialogs/TagArchiveDialog/TagArchiveDialog';
import ImportTagsDialog from '../../../components/dialogs/ImportTagsDialog/ImportTagsDialog';
import { Archive, ClipboardPaste, Plus, RotateCcw, Search } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import TagTypeSettings, { TagSettingItem } from './TagTypeSettings';

type TagsProps = {
    style?: CSSProperties;
    children?: ReactNode;
    className?: string;
};

const Tags = (_props: TagsProps) => {
    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [archiveDialog, setArchiveDialog] = useState<{ mode: 'archive' | 'restore'; ids: string[] } | null>(null);
    const [newTagVisible, setNewTagVisible] = useState(false);
    const [importTagsVisible, setImportTagsVisible] = useState(false);
    const [refreshVersion, setRefreshVersion] = useState(0);

    const activeResult = useGetTagListQuery({ variables: { contains: searchText } });
    const archivedResult = useGetArchivedTagListQuery({ variables: { contains: searchText } });

    const tags = useMemo<TagSettingItem[]>(() => [
        ...((activeResult.data?.TagList?.nodes || []).filter(Boolean) as TagSettingItem[]),
        ...((archivedResult.data?.ArchivedTagList?.nodes || []).filter(Boolean) as TagSettingItem[]),
    ], [activeResult.data, archivedResult.data]);

    const selectedActiveIds = selectedIds.filter(id => tags.some(tag => tag.id === id && !tag.isArchived));
    const selectedArchivedIds = selectedIds.filter(id => tags.some(tag => tag.id === id && !!tag.isArchived));
    const hasMore = !!activeResult.data?.TagList?.pageInfo.hasNextPage
        || !!archivedResult.data?.ArchivedTagList?.pageInfo.hasNextPage;

    const refresh = async () => {
        await Promise.all([activeResult.refetch(), archivedResult.refetch()]);
        setRefreshVersion(version => version + 1);
    };

    const handleNewTagOnClose = async () => {
        setNewTagVisible(false);
        await refresh();
    };

    const handleArchiveOnClose = async () => {
        setArchiveDialog(null);
        setSelectedIds([]);
        await refresh();
    };

    const fetchMore = async () => {
        const requests: Promise<unknown>[] = [];
        const activePage = activeResult.data?.TagList?.pageInfo;
        const archivedPage = archivedResult.data?.ArchivedTagList?.pageInfo;

        if (activePage?.hasNextPage) {
            requests.push(activeResult.fetchMore({
                variables: { contains: searchText, after: activePage.endCursor },
                updateQuery: (previous, { fetchMoreResult }) => ({
                    ...previous,
                    TagList: fetchMoreResult?.TagList && previous.TagList ? {
                        ...fetchMoreResult.TagList,
                        nodes: [...(previous.TagList.nodes || []), ...(fetchMoreResult.TagList.nodes || [])],
                    } : previous.TagList,
                }),
            }));
        }
        if (archivedPage?.hasNextPage) {
            requests.push(archivedResult.fetchMore({
                variables: { contains: searchText, after: archivedPage.endCursor },
                updateQuery: (previous, { fetchMoreResult }) => ({
                    ...previous,
                    ArchivedTagList: fetchMoreResult?.ArchivedTagList && previous.ArchivedTagList ? {
                        ...fetchMoreResult.ArchivedTagList,
                        nodes: [...(previous.ArchivedTagList.nodes || []), ...(fetchMoreResult.ArchivedTagList.nodes || [])],
                    } : previous.ArchivedTagList,
                }),
            }));
        }
        await Promise.all(requests);
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <button className="btn btn-ghost text-sm" onClick={() => setNewTagVisible(true)}>
                    <Plus size={15} /> Add tag
                </button>
                <button className="btn btn-ghost text-sm" onClick={() => setImportTagsVisible(true)}>
                    <ClipboardPaste size={15} /> Paste tags
                </button>
                <RefreshButton onRefresh={refresh} />
                <label className="relative flex-1 min-w-[220px] max-w-md ml-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
                    <input
                        className="input-base w-full"
                        aria-label="Search tags"
                        placeholder="Search tags or tag types..."
                        value={searchText}
                        onChange={event => {
                            setSearchText(event.target.value);
                            setSelectedIds([]);
                        }}
                        style={{ paddingLeft: 34 }}
                    />
                </label>

                {selectedActiveIds.length > 0 && (
                    <button
                        className="btn btn-ghost text-sm"
                        onClick={() => setArchiveDialog({ mode: 'archive', ids: selectedActiveIds })}
                    >
                        <Archive size={15} /> Archive ({selectedActiveIds.length})
                    </button>
                )}
                {selectedArchivedIds.length > 0 && (
                    <button
                        className="btn btn-ghost text-sm"
                        onClick={() => setArchiveDialog({ mode: 'restore', ids: selectedArchivedIds })}
                    >
                        <RotateCcw size={15} /> Restore ({selectedArchivedIds.length})
                    </button>
                )}
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
                <TagTypeSettings
                    tags={tags}
                    selectedIds={selectedIds}
                    onSelected={setSelectedIds}
                    onRefresh={refresh}
                    refreshVersion={refreshVersion}
                />
                {hasMore && (
                    <div className="flex justify-center px-4 pb-4">
                        <button
                            className="btn-secondary text-sm"
                            disabled={activeResult.loading || archivedResult.loading}
                            onClick={fetchMore}
                        >
                            Load more tags
                        </button>
                    </div>
                )}
            </div>

            {archiveDialog && (
                <TagArchiveDialog
                    ids={archiveDialog.ids}
                    mode={archiveDialog.mode}
                    visible
                    onClose={handleArchiveOnClose}
                />
            )}
            <NewTagDialog visible={newTagVisible} onClose={handleNewTagOnClose} />
            {importTagsVisible && (
                <ImportTagsDialog onClose={async saved => {
                    setImportTagsVisible(false);
                    if (saved) await refresh();
                }} />
            )}
        </div>
    );
};

export default Tags;
